import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Form, Button, Row, Col, Badge, Spinner,
  Alert, Toast, ToastContainer, InputGroup
} from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const KEPEMILIKAN_OPTIONS = [
  'Swasta / Rumah Sakit',
  'Pemerintah Daerah (RSUD)',
  'Kementerian Kesehatan (RSUP)',
  'TNI / POLRI',
  'BUMN',
  'Yayasan Sosial / Keagamaan',
  'Apotek Mandiri / Perorangan'
];

export default function ProfileApotek() {
  const navigate = useNavigate();

  // State Data Profil
  const [formData, setFormData] = useState({
    kode_ppk: '',
    nama_ppk: '',
    kepemilikan: 'Swasta / Rumah Sakit',
    alamat: '',
    kab_kota: '',
    telp: '',
    nama_apoteker: '',
    no_sik: ''
  });

  const [originalData, setOriginalData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Time & Notification state
  const [clockTime, setClockTime] = useState({ date: '', clock: '' });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const showNotification = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  // Clock effect
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setClockTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' WIB'
      });
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch profil dari endpoint GET /isiinstansiapot (atau /ambilinstansiapot)
  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    const apiUrl = getApiUrl();
    try {
      let res = await fetch(`${apiUrl}/isiinstansiapot`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) {
        // Fallback coba ke /ambilinstansiapot
        res = await fetch(`${apiUrl}/ambilinstansiapot`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          const d = json.data;
          const loaded = {
            kode_ppk: d.kode_ppk || '',
            nama_ppk: d.nama_ppk || '',
            kepemilikan: d.kepemilikan || 'Swasta / Rumah Sakit',
            alamat: d.alamat || '',
            kab_kota: d.kab_kota || '',
            telp: d.telp || '',
            nama_apoteker: d.nama_apoteker || '',
            no_sik: d.no_sik || ''
          };
          setFormData(loaded);
          setOriginalData(loaded);
          setIsEditMode(true);
        } else {
          // Data belum ada di profil_ppk
          setIsEditMode(false);
        }
      } else {
        showNotification(`Gagal mengambil profil apotek (HTTP ${res.status})`, 'danger');
      }
    } catch (err) {
      console.error('Error fetch profil apotek:', err);
      showNotification('Gagal terhubung ke backend SIMRS: ' + err.message, 'danger');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit Handler:
  // - Input data baru: POST /isiinstansiapot
  // - Edit / perbarui: PUT /perbaruiinstansiapot atau POST /perbaruiinstansiapot
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.kode_ppk.trim()) {
      showNotification('Kode PPK / Faskes wajib diisi!', 'warning');
      return;
    }
    if (!formData.nama_ppk.trim()) {
      showNotification('Nama Instalasi Farmasi / Apotek wajib diisi!', 'warning');
      return;
    }

    setIsSaving(true);
    const apiUrl = getApiUrl();

    try {
      let endpoint = '';
      let method = '';

      if (isEditMode) {
        // Mode edit menggunakan endpoint /perbaruiinstansiapot
        endpoint = `${apiUrl}/perbaruiinstansiapot`;
        method = 'PUT';
      } else {
        // Mode input data baru menggunakan endpoint /isiinstansiapot
        endpoint = `${apiUrl}/isiinstansiapot`;
        method = 'POST';
      }

      let res = await fetch(endpoint, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      // Jika PUT gagal pada beberapa reverse proxy, coba fallback ke POST
      if (!res.ok && method === 'PUT') {
        res = await fetch(`${apiUrl}/perbaruiinstansiapot/${encodeURIComponent(formData.kode_ppk)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
      }

      const json = await res.json();

      if (res.ok && (json.status === 'success' || json.status === 1)) {
        showNotification(
          json.message || (isEditMode ? 'Profil Apotek berhasil diperbarui!' : 'Profil Apotek baru berhasil disimpan!'),
          'success'
        );
        setOriginalData({ ...formData });
        setIsEditMode(true);
      } else {
        showNotification(json.message || json.msg || 'Gagal menyimpan profil apotek', 'danger');
      }
    } catch (err) {
      console.error('Error simpan profil apotek:', err);
      showNotification('Terjadi kesalahan koneksi: ' + err.message, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  // Switch to Input Baru Mode
  const handleModeBaru = () => {
    setFormData({
      kode_ppk: '',
      nama_ppk: '',
      kepemilikan: 'Swasta / Rumah Sakit',
      alamat: '',
      kab_kota: '',
      telp: '',
      nama_apoteker: '',
      no_sik: ''
    });
    setIsEditMode(false);
    showNotification('Silakan isi data untuk membuat Profil PPK Apotek baru.', 'info');
  };

  // Revert Changes
  const handleReset = () => {
    if (originalData) {
      setFormData({ ...originalData });
      setIsEditMode(true);
      showNotification('Perubahan form telah dibatalkan.', 'secondary');
    } else {
      handleModeBaru();
    }
  };

  const isFormChanged = originalData ? JSON.stringify(formData) !== JSON.stringify(originalData) : true;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* ══ TOAST NOTIFIKASI ══ */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={toast.show}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          delay={4000}
          autohide
          bg={toast.variant}
        >
          <Toast.Header closeButton>
            <strong className="me-auto text-white">Notifikasi SIMRS Farmasi</strong>
          </Toast.Header>
          <Toast.Body className="text-white fw-medium">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ══ TOP NAVBAR ══ */}
      <nav
        style={{
          background: 'linear-gradient(90deg, #0284c7 0%, #0369a1 100%)',
          color: 'white',
          padding: '0.65rem 1.25rem'
        }}
        className="d-flex align-items-center justify-content-between shadow-sm sticky-top"
      >
        <div className="d-flex align-items-center gap-3">
          <div style={{ background: '#ffffff22', borderRadius: '8px', padding: '0.4rem 0.55rem', display: 'flex', alignItems: 'center' }}>
            <i className="bi bi-hospital-fill text-warning fs-5"></i>
          </div>
          <div>
            <div className="fw-bold fs-6 lh-1">SIMRS — Setting Profil Instansi Apotek / Farmasi</div>
            <div className="text-light" style={{ fontSize: '0.72rem', opacity: 0.9 }}>
              Identitas Faskes, Apoteker Penanggung Jawab & Kop Dokumen Resmi Kefarmasian
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="text-light d-none d-md-inline" style={{ fontSize: '0.75rem', opacity: 0.95 }}>
            <i className="bi bi-calendar3 me-1"></i>{clockTime.date} | <i className="bi bi-clock me-1"></i>{clockTime.clock}
          </span>
          <Button
            variant="light"
            size="sm"
            className="fw-bold text-dark shadow-sm d-flex align-items-center gap-1"
            style={{ fontSize: '0.74rem', padding: '0.28rem 0.75rem', borderRadius: '6px' }}
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-house-door-fill"></i> Home
          </Button>
        </div>
      </nav>

      {/* ══ MAIN CONTAINER ══ */}
      <div className="p-3" style={{ maxWidth: '1300px', margin: '0 auto' }}>

        {/* ══ STATUS HERO CARD ══ */}
        <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: '10px', overflow: 'hidden' }}>
          <div
            className="p-3 text-white d-flex flex-wrap align-items-center justify-content-between gap-3"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  width: '54px',
                  height: '54px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.7rem'
                }}
              >
                <i className="bi bi-capsule-pill"></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0 fw-bold">{formData.nama_ppk || 'Nama Instansi Apotek Belum Diatur'}</h5>
                  <Badge bg={isEditMode ? 'success' : 'warning'} className="shadow-sm">
                    {isEditMode ? 'Profil Terdaftar' : 'Data Baru'}
                  </Badge>
                </div>
                <div style={{ fontSize: '0.82rem', opacity: 0.95 }}>
                  Kode PPK: <strong>{formData.kode_ppk || '-'}</strong> &bull; Kepemilikan: <strong>{formData.kepemilikan || '-'}</strong> &bull; Wilayah: <strong>{formData.kab_kota || '-'}</strong>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-light"
                size="sm"
                className="fw-semibold shadow-sm d-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                onClick={fetchProfile}
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-arrow-clockwise"></i>}
                Segarkan Data
              </Button>
              {isEditMode && (
                <Button
                  variant="warning"
                  size="sm"
                  className="fw-bold text-dark shadow-sm d-flex align-items-center gap-1"
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                  onClick={handleModeBaru}
                >
                  <i className="bi bi-plus-circle-fill"></i> Input Profil Baru
                </Button>
              )}
            </div>
          </div>
        </Card>

        {isLoading ? (
          <Card className="shadow-sm border-0 p-5 text-center my-4">
            <Spinner animation="border" variant="primary" className="mx-auto mb-2" />
            <div className="text-muted fw-medium">Memuat data profil instansi apotek...</div>
          </Card>
        ) : (
          <Row className="g-3">
            {/* ══ KOLOM KIRI: FORM PENGATURAN ══ */}
            <Col lg={7}>
              <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '10px' }}>
                <Card.Header className="bg-white border-bottom py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="bi bi-sliders text-primary"></i>
                      {isEditMode ? 'Form Perbarui Profil Apotek' : 'Form Input Profil Apotek Baru'}
                    </span>
                    <Badge bg={isEditMode ? 'primary' : 'success'} style={{ fontSize: '0.72rem' }}>
                      {isEditMode ? 'Mode Edit' : 'Mode Input Baru'}
                    </Badge>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  <Form onSubmit={handleSubmit}>
                    
                    {/* BAGIAN 1: IDENTITAS FASKES */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-primary border-bottom pb-2 mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="bi bi-building me-1"></i> 1. Identitas Fasilitas & Instansi Farmasi
                      </h6>

                      <Row className="g-3">
                        <Col sm={4}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Kode PPK / Faskes <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-key-fill text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="kode_ppk"
                                value={formData.kode_ppk}
                                onChange={handleChange}
                                placeholder="Contoh: A / PPK001"
                                required
                                disabled={isEditMode}
                                style={{ fontWeight: 600, color: '#0369a1' }}
                              />
                            </InputGroup>
                            {isEditMode && (
                              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                                Kunci unik PPK terkunci saat edit.
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>

                        <Col sm={8}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Nama Instansi / Instalasi Farmasi <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-hospital text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="nama_ppk"
                                value={formData.nama_ppk}
                                onChange={handleChange}
                                placeholder="Contoh: Instalasi Farmasi RS MARY CILEUNGSI"
                                required
                                style={{ fontWeight: 600 }}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Status Kepemilikan
                            </Form.Label>
                            <Form.Select
                              size="sm"
                              name="kepemilikan"
                              value={formData.kepemilikan}
                              onChange={handleChange}
                              style={{ fontSize: '0.8rem' }}
                            >
                              {KEPEMILIKAN_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Kabupaten / Kota
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-geo-alt-fill text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="kab_kota"
                                value={formData.kab_kota}
                                onChange={handleChange}
                                placeholder="Contoh: Kab. Bogor / Jakarta Timur"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                        <Col sm={12}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Nomor Telepon / Kontak Resmi Farmasi
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-telephone-fill text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="telp"
                                value={formData.telp}
                                onChange={handleChange}
                                placeholder="Contoh: (021) 8249 2222 / 0812-xxxx-xxxx"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                        <Col sm={12}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Alamat Lengkap Faskes / Farmasi
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              name="alamat"
                              value={formData.alamat}
                              onChange={handleChange}
                              placeholder="Jl. Raya Narogong KM 21 Cileungsi, Bogor, Jawa Barat"
                              style={{ fontSize: '0.8rem' }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* BAGIAN 2: PENANGGUNG JAWAB APOTEKER */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-teal border-bottom pb-2 mb-3" style={{ fontSize: '0.85rem', color: '#0f766e' }}>
                        <i className="bi bi-person-badge-fill me-1"></i> 2. Penanggung Jawab Teknis Kefarmasian (APJ)
                      </h6>

                      <Row className="g-3">
                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Nama Apoteker Penanggung Jawab (Lengkap Gelar)
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-person-fill text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="nama_apoteker"
                                value={formData.nama_apoteker}
                                onChange={handleChange}
                                placeholder="Contoh: Dada Juanda, S.Farm., Apt"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>

                        <Col sm={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-secondary" style={{ fontSize: '0.78rem' }}>
                              Nomor SIPA / SIK Apoteker
                            </Form.Label>
                            <InputGroup size="sm">
                              <InputGroup.Text className="bg-light">
                                <i className="bi bi-file-earmark-check-fill text-muted"></i>
                              </InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="no_sik"
                                value={formData.no_sik}
                                onChange={handleChange}
                                placeholder="Contoh: 19770729/SIPA_32.01/2012/1019"
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 pt-3 border-top">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleReset}
                        disabled={isSaving || !isFormChanged}
                        style={{ fontSize: '0.78rem' }}
                      >
                        <i className="bi bi-arrow-counterclockwise me-1"></i> Batal / Reset
                      </Button>

                      <Button
                        type="submit"
                        variant={isEditMode ? 'primary' : 'success'}
                        size="sm"
                        className="fw-bold shadow-sm d-flex align-items-center gap-1"
                        disabled={isSaving}
                        style={{ fontSize: '0.8rem', padding: '0.4rem 1.25rem' }}
                      >
                        {isSaving ? (
                          <><Spinner size="sm" animation="border" className="me-1" /> Menyimpan...</>
                        ) : isEditMode ? (
                          <><i className="bi bi-check2-circle fs-6"></i> Perbarui Profil Instansi</>
                        ) : (
                          <><i className="bi bi-save-fill fs-6"></i> Simpan Profil Baru</>
                        )}
                      </Button>
                    </div>

                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* ══ KOLOM KANAN: PREVIEW KOP DOKUMEN RESMI ══ */}
            <Col lg={5}>
              {/* LIVE KOP PREVIEW CARD */}
              <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: '10px' }}>
                <Card.Header className="bg-white border-bottom py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-bold text-dark d-flex align-items-center gap-2">
                      <i className="bi bi-file-earmark-text text-teal" style={{ color: '#0d9488' }}></i>
                      Pratinjau Kop Dokumen Resmi Kefarmasian
                    </span>
                    <Badge bg="secondary" style={{ fontSize: '0.68rem' }}>Live Preview</Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-3 bg-light">
                  {/* SIMULASI LEMBAR KERTAS KOP SURAT */}
                  <div
                    className="p-3 bg-white border rounded shadow-sm"
                    style={{
                      fontFamily: 'serif',
                      minHeight: '200px',
                      background: '#fff'
                    }}
                  >
                    <div className="text-center pb-2 mb-2" style={{ borderBottom: '3px double #0f172a' }}>
                      <div className="fw-bold text-uppercase" style={{ fontSize: '1rem', letterSpacing: '0.5px', color: '#0f172a' }}>
                        {formData.nama_ppk || 'INSTALASI FARMASI RUMAH SAKIT'}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.75rem', fontFamily: 'sans-serif' }}>
                        {formData.alamat || 'Alamat instansi farmasi lengkap'}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.72rem', fontFamily: 'sans-serif' }}>
                        {formData.kab_kota ? `${formData.kab_kota} ` : ''}
                        {formData.telp ? `| Telp: ${formData.telp}` : ''}
                      </div>
                    </div>

                    <div className="py-2" style={{ fontFamily: 'sans-serif', fontSize: '0.75rem' }}>
                      <div className="p-2 bg-light rounded border mb-2">
                        <div className="text-muted" style={{ fontSize: '0.68rem', fontWeight: 600 }}>APOTEKER PENANGGUNG JAWAB (APJ):</div>
                        <div className="fw-bold text-dark">{formData.nama_apoteker || 'Belum diisi'}</div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          SIPA/SIK: <span className="font-monospace text-primary">{formData.no_sik || '-'}</span>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.7rem' }}>
                        <span>Kode PPK / Faskes: <strong>{formData.kode_ppk || '-'}</strong></span>
                        <span>Status: <strong>{formData.kepemilikan || '-'}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-muted text-center" style={{ fontSize: '0.7rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Format di atas digunakan otomatis pada cetak Surat Pesanan (SP), etiket obat, dan laporan SIPNAP.
                  </div>
                </Card.Body>
              </Card>

            </Col>
          </Row>
        )}

      </div>
    </div>
  );
}
